import Alert from '~/components/alert';
import Submit from '~/components/button-submit';
import Card from '~/components/card';
import DL, { DLItem } from '~/components/definition-list';
import Form from '~/components/form';
import LineItem from '~/components/line-item';
import Page from '~/components/page';
import CardInput from '~/components/payment-summary-card-input';
import { useIsLoading } from '~/utils/routing';
import ExchangeSelection from './components/exchange-selection-component';
import RefundSelection from './components/refund-selection-component';
import ReturnSummaryDisclosure from './components/return-summary-disclosure';
import {
  ClaimValues,
  FormKey,
  useReturnSummaryActionResult,
} from './return-summary-action';
import { useReturnSummaryLoaderData } from './return-summary-loader';

type ReturnSummary = NonNullable<ReturnType<typeof useReturnSummaryLoaderData>>;
type ReturnSummaryLineItem = ReturnSummary['credits']['lineItems'][number];
type Section = ReturnSummary['credits']['sections'][number];

// -------- Helper Functions --------
const pluralizeTitle = (count: number, title: string) =>
  `${title}${count > 1 ? 's' : ''} (${count})`;

const extractSectionValue = (type: Section['type'], sections: Section[]) => {
  const section = sections.find((s) => s.type === type);
  return section ? section.amount : 0;
};

const extractSectionLabel = (type: Section['type'], sections: Section[]) => {
  const section = sections.find((s) => s.type === type);
  return section ? section.label : '';
};

// -------- Components --------

function Sections({
  sections,
  currencyFormatter,
}: {
  sections: Section[];
  currencyFormatter: (value: number) => string;
}) {
  const sectionsWithAmount = sections.filter((section) => section.amount !== 0);

  return (
    <>
      {sectionsWithAmount.map((section) => (
        <div className="flex justify-between p-2" key={section.label}>
          <span>{section.label}</span>
          <span>{currencyFormatter(section.amount)}</span>
        </div>
      ))}
    </>
  );
}

function LineItems({
  lineItems,
  title,
  currencyFormatter,
}: {
  lineItems: ReturnSummaryLineItem[];
  title: string;
  currencyFormatter: (value: number) => string;
}) {
  if (!lineItems || lineItems.length === 0) return null;

  return (
    <div className="flex flex-col gap-3 rounded-md bg-gray-50 p-2">
      <p className="text-sm">{pluralizeTitle(lineItems.length, title)}</p>
      {lineItems.map(({ unitPrice, name, options, imgUrl }) => (
        <LineItem
          name={name ?? ''}
          price={currencyFormatter(unitPrice)}
          imgSrc={imgUrl}
          options={options}
          variant="small"
        />
      ))}
    </div>
  );
}

function MonetarySection({
  outstandingBalance,
  paymentIntentSecret,
  currencyFormatter,
}: {
  outstandingBalance: ReturnSummary['outstandingBalance'];
  paymentIntentSecret?: string;
  currencyFormatter: (value: number) => string;
}) {
  if (!outstandingBalance) return null;

  // TODO: an error scenario would be a type === 'payment' but no paymentIntentSecret
  const showCardInput =
    outstandingBalance.kind === 'Payment' && !!paymentIntentSecret;

  return (
    <>
      <DL className="font-medium">
        <DLItem
          label={outstandingBalance.label}
          value={currencyFormatter(outstandingBalance.total)}
          variant="sm"
        />
      </DL>

      {showCardInput && <CardInput className="mt-2" />}
    </>
  );
}

const lineItemStyles =
  'flex flex-col gap-2 rounded-md bg-gray-50 text-sm text-gray-700';

function ReturnCredit({
  credits,
  currencyFormatter,
}: {
  credits: ReturnSummary['credits'];
  currencyFormatter: (value: number) => string;
}) {
  const { total, sections, lineItems } = credits;

  return (
    <ReturnSummaryDisclosure
      label="Return Credit"
      value={currencyFormatter(total)}
    >
      <div className={lineItemStyles}>
        <LineItems
          lineItems={lineItems}
          title="Return Item"
          currencyFormatter={currencyFormatter}
        />
        <Sections sections={sections} currencyFormatter={currencyFormatter} />
      </div>
    </ReturnSummaryDisclosure>
  );
}

function Deductions({
  deductions,
  currencyFormatter,
}: {
  deductions: ReturnSummary['credits'];
  currencyFormatter: (value: number) => string;
}) {
  const { total, sections, lineItems } = deductions;

  return (
    <ReturnSummaryDisclosure
      label="Deductions"
      value={currencyFormatter(total)}
      defaultOpen
    >
      <div className={lineItemStyles}>
        <LineItems
          lineItems={lineItems}
          title="Exchange Item"
          currencyFormatter={currencyFormatter}
        />
        <Sections sections={sections} currencyFormatter={currencyFormatter} />
      </div>
    </ReturnSummaryDisclosure>
  );
}

export default function ReturnSummary() {
  const loaderData = useReturnSummaryLoaderData();

  const currencyFormatter = (value: number) =>
    loaderData.currencyFormatter(Math.abs(value));

  const actionResult = useReturnSummaryActionResult();

  const isLoading = useIsLoading();

  const {
    credits,
    deductions,
    authorizedAmount,
    outstandingBalance,
    paymentIntentSecret,
    balance,
    exchangeMethod: {
      selected: selectedExchangeMethod,
      offered: offeredExchangeMethods,
    },
  } = loaderData;

  const exchangeSelectionRequired =
    offeredExchangeMethods.length > 1 && !selectedExchangeMethod;

  return (
    <Page
      headline="Return Summary"
      title="Review your decisions before submitting your request"
    >
      <Form className="gap-4">
        <Card headline="Return Summary" hideHeader>
          <div className="flex flex-col gap-4">
            <ReturnCredit
              credits={credits}
              currencyFormatter={currencyFormatter}
            />

            <hr />

            {deductions.total > 0 && (
              <>
                <Deductions
                  deductions={deductions}
                  currencyFormatter={currencyFormatter}
                />
                <hr />
              </>
            )}

            <ExchangeSelection />

            <RefundSelection />

            <MonetarySection
              outstandingBalance={outstandingBalance}
              paymentIntentSecret={paymentIntentSecret}
              currencyFormatter={currencyFormatter}
            />
          </div>

          <Submit
            className="mt-2"
            disabled={exchangeSelectionRequired}
            loading={isLoading}
          >
            Submit Return Request
          </Submit>

          {actionResult && !actionResult.ok && (
            <Alert
              variant="error"
              className="mt-6"
              title="An error occurred while creating the claim. Please try again later."
            >
              <p>{actionResult.message}</p>
            </Alert>
          )}
          <input
            type="hidden"
            name={FormKey.claimValues}
            value={JSON.stringify({
              fromCustomerShipping: extractSectionValue(
                'RETURN_SHIPPING',
                deductions.sections,
              ),
              fee: extractSectionValue('FEE', deductions.sections),
              feeName: extractSectionLabel('FEE', deductions.sections),
              incentive: extractSectionValue('INCENTIVE', credits.sections),
              paymentIntentSecret,
              balance,
              toCustomerShipping: extractSectionValue(
                'SHIPPING',
                deductions.sections,
              ),
              authorizedAmount,
            } satisfies ClaimValues)}
          />
        </Card>
      </Form>
    </Page>
  );
}
